import { Link, useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Music, Trophy, Users, Clock, MapPin, CheckCircle2, ArrowRight, Ticket, Star } from 'lucide-react';

const EVENTS_HERO = "https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

const EventsPage = () => {
  const navigate = useNavigate();

  const handleBookClick = (e) => {
    e.preventDefault();
    navigate('/');
    setTimeout(() => {
      const bookingSection = document.getElementById('booking');
      if (bookingSection) {
        bookingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
  };

  const venues = [
    {
      name: "Melbourne Cricket Ground (MCG)",
      events: "AFL, Cricket, Concerts",
      capacity: "100,000+"
    },
    {
      name: "Rod Laver Arena",
      events: "Australian Open, Concerts",
      capacity: "15,000"
    },
    {
      name: "Marvel Stadium",
      events: "AFL, Rugby, Concerts",
      capacity: "53,000"
    },
    {
      name: "AAMI Park",
      events: "Soccer, Rugby, Concerts",
      capacity: "30,000"
    },
    {
      name: "Sidney Myer Music Bowl",
      events: "Concerts, Carols",
      capacity: "12,000"
    },
    {
      name: "Melbourne Convention Centre",
      events: "Exhibitions, Conferences",
      capacity: "Varies"
    }
  ];

  const eventTypes = [
    {
      icon: <Trophy className="w-10 h-10" />,
      title: "Sports Events",
      description: "AFL finals, cricket tests, Australian Open tennis, Formula 1 Grand Prix – we've transported thousands of sports fans to Melbourne's biggest sporting moments. Skip the parking hassle and post-match traffic.",
      examples: ["AFL matches at MCG & Marvel Stadium", "Australian Open at Melbourne Park", "Melbourne Cup Carnival", "Formula 1 Grand Prix"]
    },
    {
      icon: <Music className="w-10 h-10" />,
      title: "Concerts & Festivals",
      description: "From intimate theatre shows to stadium-filling world tours, we provide reliable transport to Melbourne's vibrant live music scene. No worrying about parking or navigating unfamiliar areas at night.",
      examples: ["Stadium tours at MCG & Marvel", "Rod Laver Arena concerts", "Melbourne Festival events", "Comedy festival shows"]
    },
    {
      icon: <Ticket className="w-10 h-10" />,
      title: "Theatre & Arts",
      description: "Melbourne's arts precinct is world-renowned. We provide transport to the Arts Centre, theatres along Collins Street, and cultural events across the city.",
      examples: ["Melbourne Arts Centre", "Princess Theatre", "Regent Theatre", "Hamer Hall performances"]
    }
  ];

  return (
    <div data-testid="events-page">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="pt-28 pb-20 relative"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(26, 26, 26, 0.85), rgba(26, 26, 26, 0.7)), url(${EVENTS_HERO})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl text-white">
            <div className="flex items-center gap-2 mb-4">
              <Ticket className="w-5 h-5 text-primary" />
              <span className="text-xs font-bold uppercase tracking-widest text-primary">Events & Entertainment</span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight">
              Sports, Concerts & <span className="text-primary">Events</span> Transport
            </h1>
            <p className="mt-6 text-gray-300 text-lg leading-relaxed">
              Melbourne is Australia's events capital – and we're here to get you there and back safely. 
              Whether it's the footy at the 'G, a concert at Rod Laver, or a night at the theatre, 
              skip the parking stress and enjoy the event knowing your ride home is sorted.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <button 
                onClick={handleBookClick}
                className="bg-primary hover:bg-primary-hover text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center gap-2"
              >
                Book Event Transport
                <ArrowRight className="w-4 h-4" />
              </button>
              <a 
                href="tel:1300096685"
                className="bg-white/10 hover:bg-white/20 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200 border border-white/20"
              >
                Call 1300 096 685
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Event Types */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-widest text-primary">Event Transport</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mt-2">
              We Cover Every Event
            </h2>
          </div>
          <div className="space-y-12">
            {eventTypes.map((type, index) => (
              <div 
                key={index}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center ${
                  index % 2 === 1 ? '' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="bg-secondary text-white p-8 rounded-2xl">
                    <div className="w-16 h-16 rounded-xl bg-primary/20 text-primary flex items-center justify-center mb-4">
                      {type.icon}
                    </div>
                    <h3 className="font-heading text-2xl font-bold mb-4">{type.title}</h3>
                    <p className="text-gray-400 leading-relaxed mb-6">{type.description}</p>
                    <div className="space-y-2">
                      {type.examples.map((example, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                          <span className="text-gray-300 text-sm">{example}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="space-y-4">
                    <h3 className="font-heading text-2xl font-bold text-secondary">
                      Why Choose Us for {type.title}?
                    </h3>
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <Star className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">Pre-book your return journey and your driver will be waiting when you exit</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Star className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">Avoid surge pricing from rideshare apps after major events</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Star className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">Maxi taxis available for groups attending together</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Star className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">Drivers know the best pickup points and avoid traffic congestion</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Venues */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-widest text-primary">Venues</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mt-2">
              Melbourne's Major Venues
            </h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
              We regularly transport passengers to and from all of Melbourne's premier event venues. 
              Our drivers know the best drop-off and pickup points to get you as close as possible.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {venues.map((venue, index) => (
              <div 
                key={index}
                className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200"
              >
                <div className="flex items-start gap-3 mb-3">
                  <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading text-lg font-bold text-secondary">{venue.name}</h3>
                    <p className="text-sm text-muted-foreground">{venue.events}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Users className="w-4 h-4" />
                  <span>Capacity: {venue.capacity}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tips */}
      <section className="py-16 bg-secondary text-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl font-bold">
              Event Transport Tips
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                title: "Book in Advance",
                tip: "For major events like AFL finals or big concerts, book your taxi 1-2 days ahead to guarantee availability."
              },
              {
                title: "Schedule Your Return",
                tip: "Don't get stranded. Pre-book your return trip with an estimated pickup time after the event ends."
              },
              {
                title: "Know the Pickup Point",
                tip: "Our drivers will guide you to the best meeting spot – usually away from the main crowd for faster pickup."
              },
              {
                title: "Travel as a Group",
                tip: "Going with friends? Book a maxi taxi – it's often cheaper than multiple cars and way more fun."
              }
            ].map((item, index) => (
              <div key={index} className="bg-white/5 p-6 rounded-xl border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-5 h-5 text-primary" />
                  <h3 className="font-heading text-lg font-bold">{item.title}</h3>
                </div>
                <p className="text-gray-400 text-sm">{item.tip}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-black mb-4">
            Got an Event Coming Up?
          </h2>
          <p className="text-black/80 mb-8 max-w-2xl mx-auto">
            Don't let transport stress ruin your event experience. Book your taxi now and 
            focus on enjoying the game, concert, or show.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={handleBookClick}
              className="bg-secondary hover:bg-secondary/90 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
            >
              Book Your Ride
            </button>
            <a 
              href="tel:1300096685"
              className="bg-white hover:bg-gray-100 text-secondary px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
            >
              Call 1300 096 685
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EventsPage;
